import { FC, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';

import { getUserFullName } from '~src/interfaces/user.interface';
import { SideBarItemI, SubNavSection } from '~src/shared/sidebar/constants/sideBarData';
import { StoreI } from '~src/rootReducer';
import { SessionUserI } from '~src/interfaces/session.types';
import { CompanyRespWithSettingAttributes } from '~src/interfaces/company.interface';
import { CycleI } from '~src/interfaces/cycle.interface';
import { Container, IconWrapper } from '~src/shared/sidebar/styles/sidebarItem.sc';

import ListSubNav from './ListSubNav';
import { CompanySideBarItem, LinkedIconBarItem, SimpleIconBarItem, UserSideBarItem } from './SidebarIcons';
import Search from './Search';

interface OwnProps {
  menuItem: SideBarItemI;
  isSelected: boolean;
  selectItem: (name: string) => void;
  deSelectSearch: () => void;
  isAdmin: boolean;
  viewBoxWidth?: number;
  hideMenuIfEmpty?: boolean;
}

interface StateProps {
  sessionUser: SessionUserI | null;
  sessionCompany: CompanyRespWithSettingAttributes | null;
  cycles: { [key: string]: CycleI } | null;
  isNotNewUser: boolean;
}

type Props = OwnProps & StateProps;

const SidebarItem: FC<Props> = (props) => {
  const defaultUrl: string = useMemo(() => {
    const { url, sectionLinks } = props.menuItem;
    const links: SubNavSection[] = typeof sectionLinks === 'function' ? sectionLinks() : sectionLinks || [];

    if (url) {
      return url;
    } else if (links && links.length > 0 && links[0].items.length > 0) {
      return links[0].items[0].url || '';
    } else {
      return '';
    }
  }, [props.menuItem]);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const getSubNav = useCallback(() => {
    const { isSelected, cycles, menuItem, deSelectSearch, sessionUser, selectItem, sessionCompany, hideMenuIfEmpty } = props;
    const sharedProps = { menuItem, isSelected, hideMenuIfEmpty };
    if (sessionCompany && menuItem.name === 'company') {
      sharedProps.menuItem = { ...menuItem, name: sessionCompany.name };
    }
    if (sessionUser && menuItem.name === 'user') {
      sharedProps.menuItem = {
        ...menuItem,
        name: getUserFullName(sessionUser),
      };
    }

    if (menuItem.name === 'search') {
      return <Search {...sharedProps} deSelectSearch={deSelectSearch} selectItem={selectItem} />;
    }

    if (menuItem.name === 'content') {
      return (
        <ListSubNav
          {...sharedProps}
          cycles={cycles ? Object.values(cycles) : []}
          offset={containerRef.current && containerRef.current.getBoundingClientRect().top}
          defaultUrl={defaultUrl}
        />
      );
    }

    return (
      <ListSubNav
        {...sharedProps}
        cycles={cycles ? Object.values(cycles) : []}
        offset={containerRef.current && containerRef.current.getBoundingClientRect().top}
        defaultUrl={defaultUrl}
      />
    );
  }, [props.menuItem, props.cycles, props.isSelected]);

  const getSideBarIcon = useCallback(() => {
    const sharedProps = {
      isSelected: props.isSelected,
      menuItem: props.menuItem,
      viewBoxWidth: props.viewBoxWidth,
      defaultUrl,
    };

    const showRedDot = !props.isNotNewUser && props.isAdmin;

    if (props.sessionUser && props.sessionCompany) {
      switch (props.menuItem.name) {
        case 'company':
          return (
            <CompanySideBarItem showRedDot={showRedDot} company={props.sessionCompany} {...sharedProps} />
          );
        case 'user':
          return <UserSideBarItem user={props.sessionUser} {...sharedProps} />;
        case 'search':
          return <SimpleIconBarItem {...sharedProps} />;
        default:
          return <LinkedIconBarItem {...sharedProps} />;
      }
    }
  }, [
    props.sessionUser,
    props.sessionCompany,
    props.menuItem,
    props.isSelected,
    props.isAdmin,
    props.isNotNewUser,
  ]);

  const handleMouseEnter = () => {
    props.selectItem(props.menuItem.name);
  };

  const handleMouseLeave = () => {
    props.selectItem('');
    props.deSelectSearch();
  };

  return (
    <Container
      ref={containerRef}
      onClick={handleMouseEnter}
      // Reason for existence of mouse enter/leave: https://loopline-systems.atlassian.net/l/cp/1UwCPpuH
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      $isSelected={props.isSelected}
      style={props.menuItem.style}
    >
      <IconWrapper>{getSideBarIcon()}</IconWrapper>
      {getSubNav()}
    </Container>
  );
};

const mapStateToProps = ({ user, app }: StoreI) => {
  return {
    sessionUser: user.session.user,
    sessionCompany: user.session.company,
    cycles: user.cycles.byId,
    isNotNewUser: app.isNotNewUser,
  };
};

const SidebarItemSuper = connect(mapStateToProps)(SidebarItem);
export default SidebarItemSuper;
